import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createVNode as _createVNode, withCtx as _withCtx, unref as _unref, mergeProps as _mergeProps, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, Fragment as _Fragment, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { class: "modal-info" }
const _hoisted_2 = { class: "symptom-context-edit-textarea-container" }
const _hoisted_3 = { class: "modal-footer-buttons" }

import {
  ref,
  reactive,
  getCurrentInstance,
} from "vue";
import { processing, processend } from "@/utils/view";
import { _$t } from "@/utils/i18n";

import Modal from "@/views/base/ModalComponent.vue";
import { NoteData, NoteType } from "./types";
import TextArea from "@/views/base/CharCounterTextArea.vue";
import ClinicNote from "@/models/myRecord/clinicNote/clinicNote";

import ConfirmDialog from "@/views/base/CommonMessage.vue";
import MessageDialog from "@/views/base/CommonMessage.vue";
import ErrorDialog from "@/views/base/CommonMessage.vue";

// --- props ----------------------------------------
const componentId = "healthcaredata02-01-e02";

export default /*@__PURE__*/_defineComponent({
  __name: 'EditSingleNote',
  props: {
    title: {},
    description: {},
    hospitalVisitId: {},
    type: {},
    clinicNote: {}
  },
  emits: ["save", "close"],
  setup(__props: any, { emit: __emit }) {

const props = __props;

// --- Constants & Variables ------------------------------
const title = ref(props.title);
const description = ref(props.description);

// 診察ノート
const localNote = ref<NoteData>(
  props.clinicNote ?? { content: "", input_date: "", sub_number: 0 }
);
//  notify Event
const emit = __emit;

// ▼ トースト表示用
const { appContext } = getCurrentInstance()!;
const $toast = appContext.config.globalProperties.$toast;

// --- Dialogs -------------------------------------------

// 確認ダイアログ関連情報
let confirmDialogSet = reactive({
  show: false,
  okButtonHide: true,
  okButton: true,
  okName: _$t("clinic_note_multi_edit_button_ok"),
  cancelButton: true,
  cancelName: _$t("clinic_note_multi_edit_button_cancel"),
  message: "", // 表示メッセージ
  open: (message: string): void => {
    processing();
    confirmDialogSet.message = message;
    confirmDialogSet.show = true;
  },
  execute: async (): Promise<void> => {
    processend();
    confirmDialogSet.show = false;
    await save();
    // トースト表示
    $toast({
      // ▼ 表示メッセージ
      //   PCの場合はメッセージ内の<br>が消えます
      message: `${props.title}${_$t("common_message_saved")}`,
      // ▼ 位置指定する場合は設定（単位: px）
      //   指定なしの場合
      //    【PC】表示位置
      //       X:画面左から56px
      //       Y:画面下から20px

      //    【SP】表示位置
      //       X:画面左から16px
      //       Y:画面下から84px（フッターナビから20px上）
      //
      // offsetX: 0,
      // offsetY: 0,
      // ▼ タイムアウト値を変える場合は設定（単位: ms）
      //   デフォルトは2500ms
      // timer: 30000,
      icon: "success",
    });
    emit("save", props.type, localNote.value);
  },
  close: (): void => {
    processend();
    confirmDialogSet.show = false;
  },
});

// メッセージダイアログ関連情報
let messageDialogSet = reactive({
  show: false,
  okButton: true,
  okButtonHide: true,
  okName: _$t("clinic_note_multi_edit_button_ok"), // OK
  message: "",
  open: (message: string): void => {
    processing();
    messageDialogSet.message = message;
    messageDialogSet.show = true;
  },
  close: (): void => {
    processend();
    // 診察ノートへ反映
    emit("save", props.type, localNote.value);
    messageDialogSet.show = false;
  },
});

// エラーダイアログ関連情報
const sessionErr = ref(false);
let errorDialogSet = reactive({
  show: false,
  okButtonHide: true,
  safe: false,
  message: "",
  open: (message: string): void => {
    processing(); // オーバレイを表示
    errorDialogSet.message = message;
    errorDialogSet.show = true;
  },
  close: (): void => {
    processend(); // オーバレイを非表示
    errorDialogSet.show = false;
    errorDialogSet.message = "";
    if (sessionErr.value) {
      window.location.replace("/cms/login");
    }
  },
});

// 保存
async function save() {
  // 診察ノートモデル作成
  const clinicNoteModel = new ClinicNote();

  // 未入力ノートは保存対象から除外する
  const note = localNote.value;
  const localNoteToSave = note?.content?.trim()
    ? JSON.stringify([note])
    : JSON.stringify([]);

  // パラメータ設定
  const params = {
    hospitalVisitId: props.hospitalVisitId,
    type: props.type,
    clinicNotes: localNoteToSave,
  };
  clinicNoteModel.set(params);

  // 保存
  const [_model, response, err] = await clinicNoteModel.saveNote();

  // エラー判定
  if (err) {
    const status = err.error.response.status;
    const decodedMessage = decodeURIComponent(
      err.error?.response?.data?.message
    );
    // セッションエラー
    if (status === 408) sessionErr.value = true;
    errorDialogSet.open(decodedMessage);
    return;
  }
  // データ更新
  if (response.data[0]) {
    localNote.value = response.data[0];
  }
  // 完了メッセージ
  messageDialogSet.open(_$t("clinic_note_message_saved"));
}

// --- Event Handlers -------------------------------------------
function onClose() {
  emit("close");
}

async function onSave() {
  await save();
  // トースト表示
  $toast({
    // ▼ 表示メッセージ
    //   PCの場合はメッセージ内の<br>が消えます
    message: `${props.title}${_$t("common_message_saved")}`,
    // ▼ 位置指定する場合は設定（単位: px）
    //   指定なしの場合
    //    【PC】表示位置
    //       X:画面左から56px
    //       Y:画面下から20px

    //    【SP】表示位置
    //       X:画面左から16px
    //       Y:画面下から84px（フッターナビから20px上）
    //
    // offsetX: 0,
    // offsetY: 0,
    // ▼ タイムアウト値を変える場合は設定（単位: ms）
    //   デフォルトは2500ms
    // timer: 30000,
    icon: "success",
  });
  emit("save", props.type, localNote.value);
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createVNode(Modal, {
      id: componentId,
      title: `${title.value}${_ctx.$t('common_title_edit')}`,
      showTab: false,
      modalClass: "modal-symptom-context-edit",
      onClose: onClose
    }, {
      bodyInfo: _withCtx(() => [
        _createElementVNode("p", _hoisted_1, _toDisplayString(description.value), 1),
        _createElementVNode("div", _hoisted_2, [
          _createVNode(TextArea, {
            modelValue: localNote.value.content,
            "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((localNote.value.content) = $event)),
            textareaClass: "symptom-context-edit-textarea",
            charCounterClass: "symptom-context-edit-textarea-count",
            placeholder: _ctx.$t('clinic_note_single_edit_placeholder_note'),
            width: "640px",
            minHeight: "300px",
            maxlength: 200
          }, null, 8, ["modelValue", "placeholder"])
        ])
      ]),
      footerButton: _withCtx(() => [
        _createElementVNode("div", _hoisted_3, [
          _createElementVNode("button", {
            onClick: onClose,
            type: "button",
            class: "modal-cansel"
          }, _toDisplayString(_ctx.$t("clinic_note_single_edit_button_cancel")), 1),
          _createElementVNode("button", {
            onClick: onSave,
            type: "button",
            class: "modal-ok"
          }, _toDisplayString(_ctx.$t("clinic_note_single_edit_button_save")), 1)
        ])
      ]),
      _: 1
    }, 8, ["title"]),
    (_unref(confirmDialogSet).show)
      ? (_openBlock(), _createBlock(ConfirmDialog, _mergeProps({ key: 0 }, _unref(confirmDialogSet), {
          onOk: _unref(confirmDialogSet).execute,
          onCancel: _unref(confirmDialogSet).close
        }), null, 16, ["onOk", "onCancel"]))
      : _createCommentVNode("", true),
    (_unref(messageDialogSet).show)
      ? (_openBlock(), _createBlock(MessageDialog, _mergeProps({ key: 1 }, _unref(messageDialogSet), {
          onOk: _unref(messageDialogSet).close,
          onCancel: _unref(messageDialogSet).close
        }), null, 16, ["onOk", "onCancel"]))
      : _createCommentVNode("", true),
    (_unref(errorDialogSet).show)
      ? (_openBlock(), _createBlock(ErrorDialog, _mergeProps({ key: 2 }, _unref(errorDialogSet), {
          onOk: _unref(errorDialogSet).close,
          onCancel: _unref(errorDialogSet).close
        }), null, 16, ["onOk", "onCancel"]))
      : _createCommentVNode("", true)
  ], 64))
}
}

})